import { PREDEFINED_INSURANCE_VERIFICATION_FILTERS } from "../../status";
import type { SortModel } from "../DataTable";
import { FieldConverter } from "./utils/fieldUtils";

export const INSURANCE_VERIFICATION_PERSIST_KEY =
  "insuranceVerificationListView";

export const getDefaultSortModel = (
  filter: PREDEFINED_INSURANCE_VERIFICATION_FILTERS,
): SortModel =>
  filter === PREDEFINED_INSURANCE_VERIFICATION_FILTERS.ALL
    ? [{ field: "postedDateTime", sort: "desc" }]
    : [{ field: "appointmentDatetime", sort: "asc" }];

const convertBooleanField = (
  newValue: boolean | undefined,
  oldValue: string | null,
): string | null => {
  if (newValue !== undefined) {
    return FieldConverter.booleanToString(newValue);
  }
  return oldValue;
};

export const applyVerificationUpdate = (
  verification: InsuranceVerificationMessage,
  update: InsuranceVerificationUpdateMessage,
): InsuranceVerificationMessage => ({
  ...verification,
  ...update,
  hasOrthoPlanDeductible: convertBooleanField(
    update.hasOrthoPlanDeductible,
    verification.hasOrthoPlanDeductible,
  ),
  hasOrthoWaitingPeriod: convertBooleanField(
    update.hasOrthoWaitingPeriod,
    verification.hasOrthoWaitingPeriod,
  ),
});

export type InsuranceVerificationStore = {
  selectedFilter: PREDEFINED_INSURANCE_VERIFICATION_FILTERS;
  includeAutoFiltered: boolean;
  currentDetail?: InsuranceVerificationMessage;
  detailLoading: boolean;
  detailError?: string;
};

export const initialInsuranceVerificationStore: InsuranceVerificationStore = {
  selectedFilter: PREDEFINED_INSURANCE_VERIFICATION_FILTERS.ALL,
  includeAutoFiltered: false,
  detailLoading: false,
};

export type InsuranceVerificationStoreAction =
  | {
      type: "VERIFICATION_POSTED_STATUS_CHANGED";
      verificationToUpdate: InsuranceVerificationMessage;
    }
  | {
      type: "FILTER_CHANGED";
      filter: PREDEFINED_INSURANCE_VERIFICATION_FILTERS;
    }
  | {
      type: "VERIFICATION_UPDATED";
      verificationUpdate: InsuranceVerificationUpdateMessage;
    }
  | {
      type: "DETAIL_LOADING";
    }
  | {
      type: "DETAIL_LOADED";
      detail: InsuranceVerificationMessage;
    }
  | {
      type: "DETAIL_ERROR";
      error: string;
    }
  | {
      type: "DETAIL_RESET";
    }
  | {
      type: "AUTOMATION_RESULT";
      result: VerificationAutomationResult;
    }
  | {
      type: "INCLUDE_AUTO_FILTERED";
      includeAutoFiltered: boolean;
    };

export function insuranceVerificationStoreReducer(
  store: InsuranceVerificationStore,
  action: InsuranceVerificationStoreAction,
): InsuranceVerificationStore {
  switch (action.type) {
    case "VERIFICATION_UPDATED":
      return {
        ...store,
        currentDetail:
          store.currentDetail?.wieldyId === action.verificationUpdate.wieldyId
            ? applyVerificationUpdate(
                store.currentDetail,
                action.verificationUpdate,
              )
            : store.currentDetail,
      };
    case "VERIFICATION_POSTED_STATUS_CHANGED":
      return {
        ...store,
        currentDetail:
          store.currentDetail?.wieldyId === action.verificationToUpdate.wieldyId
            ? action.verificationToUpdate
            : store.currentDetail,
      };
    case "FILTER_CHANGED":
      return {
        ...store,
        selectedFilter: action.filter,
      };
    case "DETAIL_LOADING":
      return {
        ...store,
        detailLoading: true,
        detailError: undefined,
      };
    case "DETAIL_LOADED":
      return {
        ...store,
        currentDetail: action.detail,
        detailLoading: false,
        detailError: undefined,
      };
    case "DETAIL_ERROR":
      return {
        ...store,
        detailLoading: false,
        detailError: action.error,
        currentDetail: undefined,
      };
    case "DETAIL_RESET":
      return {
        ...store,
        currentDetail: undefined,
        detailLoading: false,
        detailError: undefined,
      };
    case "AUTOMATION_RESULT": {
      const automationDetailUpdate = store.currentDetail
        ? {
            ...store.currentDetail,
            verificationOutcome:
              action.result.verificationOutcome ||
              store.currentDetail.verificationOutcome,
            verificationStatus:
              action.result.verificationStatus ||
              store.currentDetail.verificationStatus,
            verifiedAt:
              action.result.verifiedAt || store.currentDetail.verifiedAt,
          }
        : undefined;

      return {
        ...store,
        currentDetail: automationDetailUpdate,
      };
    }
    case "INCLUDE_AUTO_FILTERED":
      return {
        ...store,
        includeAutoFiltered: action.includeAutoFiltered,
      };
    default:
      return store;
  }
}
